import useBulkActions from "@entities/brief/lib/hooks/useBulkActions";
import { ToastService } from "@shared/lib/services";
import {
  BriefNotificationActionType,
  IBulkActionRequest,
} from "@shared/lib/types/brief";
import { IAssignedPOC } from "@shared/lib/types/briefInfluencerV2";
import { useEffect, useState } from "react";
import { getExecutionSelectedCount } from "./buildCampaignBulkFilterParams";

type UseCampaignExecutionBulkArgs = {
  briefId: string;
  totalCount?: number;
  filterParams: IBulkActionRequest["filterParams"];
  resetKey: string;
};

export function useCampaignExecutionBulk({
  briefId,
  totalCount,
  filterParams,
  resetKey,
}: UseCampaignExecutionBulkArgs) {
  const [selectAll, setSelectAll] = useState(false);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [uncheckList, setUncheckList] = useState<string[]>([]);
  const [isAttachBriefOpen, setIsAttachBriefOpen] = useState(false);
  const [isAttachScriptOpen, setIsAttachScriptOpen] = useState(false);
  const [opsPocModalProps, setOpsPocModalProps] = useState<{
    visible: boolean;
    selectedAssignedTo?: IAssignedPOC["assignedTo"];
  }>({ visible: false });

  const {
    bulkActions,
    isBulkActions,
    isBulkActionsSuccess,
    bulkActionsError,
  } = useBulkActions();

  const clearSelection = () => {
    setSelectAll(false);
    setCheckedList([]);
    setUncheckList([]);
  };

  useEffect(() => {
    clearSelection();
  }, [resetKey]);

  useEffect(() => {
    if (isBulkActionsSuccess) {
      ToastService.success("Action started. Track progress in notifications.");
      setIsAttachBriefOpen(false);
      setIsAttachScriptOpen(false);
      setOpsPocModalProps({ visible: false });
      clearSelection();
    } else if (bulkActionsError) {
      ToastService.error(bulkActionsError.message);
    }
  }, [isBulkActionsSuccess, bulkActionsError]);

  const selectedCount = getExecutionSelectedCount(
    selectAll,
    checkedList.length,
    uncheckList.length,
    totalCount
  );
  const hasSelection = selectAll || checkedList.length > 0;

  const bulkPayload = {
    briefId,
    ids: selectAll ? [] : checkedList,
    notIncludeIds: selectAll ? uncheckList : [],
    isSelectAll: selectAll,
    filterParams,
  };

  const openAttachBrief = () => {
    if (!hasSelection) {
      setSelectAll(true);
    }
    setIsAttachBriefOpen(true);
  };

  const openAttachScript = () => {
    if (!hasSelection) {
      setSelectAll(true);
    }
    setIsAttachScriptOpen(true);
  };

  const assignOpsPoc = (assignedToUserId: string) => {
    bulkActions({
      ...bulkPayload,
      actionType:
        BriefNotificationActionType.assignOperationsPocToCampaignInfluencer,
      actionParams: { assignedToUserId },
    });
  };

  const attachBrief = (taskId: string, title: string) => {
    bulkActions({
      ...bulkPayload,
      actionType:
        BriefNotificationActionType.attachBriefToCampaignInfluencerTask,
      actionParams: {
        attachBriefToCampaignInfluencerTaskParams: {
          taskId,
          form: { title },
        },
      },
    });
  };

  const attachScript = (taskId: string, copyFromScriptId: string) => {
    bulkActions({
      ...bulkPayload,
      actionType:
        BriefNotificationActionType.editOrAddScriptToCampaignInfluencerTask,
      actionParams: {
        editOrAddScriptToCampaignInfluencerTaskParams: {
          taskId,
          copyFromScriptId,
        },
      },
    });
  };

  return {
    selectAll,
    checkedList,
    uncheckList,
    setSelectAll,
    setCheckedList,
    setUncheckList,
    selectedCount,
    hasSelection,
    clearSelection,
    isAttachBriefOpen,
    setIsAttachBriefOpen,
    openAttachBrief,
    isAttachScriptOpen,
    setIsAttachScriptOpen,
    openAttachScript,
    opsPocModalProps,
    setOpsPocModalProps,
    isBulkActions,
    assignOpsPoc,
    attachBrief,
    attachScript,
  };
}
